/**
 * AdminHubPage — card-based landing page for admin features.
 *
 * Route: /oneadvanced-admin
 * Access: gated by catalogEntityCreatePermission (backstage-admins group)
 */

import { makeStyles } from 'tss-react/mui';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import { Settings as SettingsIcon } from '@mui/icons-material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Page, Header, Content, Link } from '@backstage/core-components';
import { adminCards } from './adminCards';
import type { AdminCard } from './types';

const useStyles = makeStyles()((theme) => ({
  section: { marginBottom: theme.spacing(5) },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2.5),
    paddingBottom: theme.spacing(1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  sectionTitle: { fontWeight: 700, fontSize: 17 },
  sectionDesc: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    marginTop: 3,
  },
  sectionIcon: {
    width: 38,
    height: 38,
    borderRadius: '50%',
    backgroundColor:
      theme.palette.mode === 'dark' ? 'rgba(244,121,32,0.22)' : '#FDE8F5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  subCard: {
    height: '100%',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    transition: 'box-shadow 0.18s, transform 0.14s',
    '&:hover': { boxShadow: theme.shadows[4], transform: 'translateY(-2px)' },
  },
  subCardContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
  },
  subCardText: { flex: 1 },
  subCardTitle: { fontWeight: 700, fontSize: 14 },
  subCardDesc: { fontSize: 12, color: theme.palette.text.secondary, marginTop: 2 },
}));

function AdminSection({ card }: { card: AdminCard }) {
  const { classes } = useStyles();
  const Icon = card.icon ?? SettingsIcon;
  return (
    <div className={classes.section}>
      <div className={classes.sectionHeader}>
        <div className={classes.sectionIcon}>
          <Icon style={{ fontSize: 20, color: '#E6007E' }} />
        </div>
        <div>
          <Typography className={classes.sectionTitle}>{card.title}</Typography>
          {card.description && (
            <Typography className={classes.sectionDesc}>
              {card.description}
            </Typography>
          )}
        </div>
      </div>
      <Grid container spacing={2}>
        {card.subCards.map(sub => (
          <Grid key={sub.to} item xs={12} sm={6} md={4}>
            <Card className={classes.subCard} elevation={0}>
              <CardActionArea
                component={Link}
                to={sub.to}
                style={{ display: 'block', textDecoration: 'none' }}
              >
                <CardContent className={classes.subCardContent}>
                  <div className={classes.subCardText}>
                    <Typography className={classes.subCardTitle}>
                      {sub.title}
                    </Typography>
                    {sub.description && (
                      <Typography className={classes.subCardDesc}>
                        {sub.description}
                      </Typography>
                    )}
                  </div>
                  <ChevronRightIcon
                    style={{ fontSize: 20, opacity: 0.5, flexShrink: 0 }}
                  />
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </div>
  );
}

export function AdminHubPage() {
  return (
    <Page themeId="tool">
      <Header
        title="Admin Hub"
        subtitle="Manage OneAdvanced portal configuration and features."
      />
      <Content>
        {adminCards.length === 0 ? (
          <Typography variant="body2" style={{ opacity: 0.6 }}>
            No admin features registered yet.
          </Typography>
        ) : (
          [...adminCards]
            .sort((a, b) => a.title.localeCompare(b.title))
            .map(card => <AdminSection key={card.title} card={card} />)
        )}
      </Content>
    </Page>
  );
}
